import { useMutation, useQueryClient } from "@tanstack/react-query";

import { POST_ERRORS } from "@/types/error";

import keys from "./keys";
import { Area, Gateway, Post } from "./types";
import { useApi } from "../client";
import keysClients from "../clients/keys";

export type CreateAreaValues = {
  name: string;
  gateways: Gateway[];
  clientId: string;
};

export type UpdateAreaValues = {
  name?: string;
  gateways: Gateway[];
  posts: Post[];
};

export const useCreateArea = ({ closeModal }: { closeModal: () => void }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async (variables: CreateAreaValues) => {
      const requestOptions = {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(variables),
      };
      const response = await api.post("areas", requestOptions);

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["clients"],
      });
      closeModal();
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const useUpdateArea = (areaId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async (variables: Partial<UpdateAreaValues>) => {
      const requestOptions = {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(variables),
      };
      const response = await api.patch(
        `areas/${areaId}/update`,
        requestOptions
      );

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKey,
      });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

interface CreatePostVariables {
  address: number;
  latitude?: string;
  longitude?: string;
}

import { HTTPError } from "ky";

export const useCreatePost = ({
  clientId,
  areaId,
}: {
  clientId: string;
  areaId: string;
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async (variables: CreatePostVariables) => {
      const requestOptions = {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(variables),
      };

      return api.patch(`areas/${areaId}/posts`, requestOptions).json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: keysClients.one(clientId),
        exact: true,
      });
    },
    onError: async (error: unknown) => {
      if (error instanceof HTTPError) {
        const body = await error.response.json();

        switch (error.response.status) {
          case 409:
            console.warn(POST_ERRORS.ALREADY_EXISTS, body?.message);
            break;
          default:
            console.error(POST_ERRORS.UNKNOWN, body?.message);
        }
        return;
      }

      // Erreur réseau ou inattendue (pas une HTTPError ky)
      console.error(POST_ERRORS.UNKNOWN, error);
    },
  });
};

export const useProblemOnPost = ({
  areaId,
  onClose,
}: {
  areaId: string;
  onClose: () => void;
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.one(areaId);

  return useMutation({
    mutationFn: async ({
      postName,
      problem,
    }: {
      postName: string;
      problem: string;
    }) => {
      const requestOptions = {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          postName: postName,
          problem: problem,
          statusToChange: "problem",
        }),
      };
      const response = await api.patch(`areas/${areaId}/post`, requestOptions);
      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      onClose();
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const usePostStatusReset = (areaId: Area["_id"]) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.one(areaId);
  return useMutation({
    mutationFn: async ({ postName }: { postName: string }) => {
      const requestOptions = {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postName: postName }),
      };
      const response = await api.patch(`areas/${areaId}/post`, requestOptions);
      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};
